import { Box, Card, Typography, useTheme } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

const SCORED_METRICS = [
  { key: "periodReturn", higherIsBetter: true },
  { key: "relativeReturn", higherIsBetter: true },
  { key: "annualizedReturn", higherIsBetter: true },
  { key: "sharpeRatio", higherIsBetter: true },
  { key: "sortinoRatio", higherIsBetter: true },
  // maxDrawdown stored as negative: -13.32 > -15.67, so higher stored value = less drawdown = better
  { key: "maxDrawdown", higherIsBetter: true },
  { key: "twr", higherIsBetter: true },
];

/**
 * Enriches strategies with the computed relativeReturn value.
 * Main (index 0) gets its return relative to the first compare.
 * Each compare gets its return relative to main.
 */
const enrichWithRelativeReturn = (strategies) => {
  const mainReturn = strategies[0]?.periodReturn;
  return strategies.map((strategy, index) => {
    const compareReturn = index === 0 ? strategies[1]?.periodReturn : mainReturn;
    const relativeReturn = strategy.periodReturn != null && compareReturn != null
      ? strategy.periodReturn - compareReturn
      : null;
    return { ...strategy, relativeReturn };
  });
};

/**
 * Compute wins for main (index 0) and compare (index 1) across SCORED_METRICS.
 * Returns { mainWins, compareWins, total } so the caller can display the leader's actual count.
 */
const computeWins = (strategies) => {
  if (strategies.length < 2) return { mainWins: 0, compareWins: 0, total: SCORED_METRICS.length };

  const enriched = enrichWithRelativeReturn(strategies);
  const main = enriched[0];
  const compare = enriched[1];
  let mainWins = 0;
  let compareWins = 0;

  SCORED_METRICS.forEach(({ key, higherIsBetter }) => {
    const mainValue = main[key];
    const compareValue = compare[key];
    if (mainValue == null || compareValue == null) return;

    if (higherIsBetter ? mainValue > compareValue : mainValue < compareValue) {
      mainWins++;
    } else if (higherIsBetter ? compareValue > mainValue : compareValue < mainValue) {
      compareWins++;
    }
  });

  return { mainWins, compareWins, total: SCORED_METRICS.length };
};

const formatDelta = (value, decimals = 2) => {
  if (value == null) return "-";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(decimals)}`;
};

/**
 * ComparisonSummary
 *
 * Shows which strategy leads overall and three key differentiators.
 * Designed for N strategies: uses main vs all comparisons.
 */
const ComparisonSummary = ({ strategies }) => {
  const theme = useTheme();

  if (strategies.length < 2) return null;

  const main = strategies[0];
  const firstCompare = strategies[1];

  const { mainWins, compareWins, total } = computeWins(strategies);
  const leaderIsMain = mainWins >= compareWins;
  const leader = leaderIsMain ? main : firstCompare;
  const trailer = leaderIsMain ? firstCompare : main;
  const displayWins = leaderIsMain ? mainWins : compareWins;

  const returnDelta = main?.periodReturn != null && firstCompare?.periodReturn != null
    ? ((main.periodReturn - firstCompare.periodReturn))
    : null;

  const sharpeDelta = main?.sharpeRatio != null && firstCompare?.sharpeRatio != null
    ? (main.sharpeRatio - firstCompare.sharpeRatio)
    : null;

  const drawdownDelta = main?.maxDrawdown != null && firstCompare?.maxDrawdown != null
    ? (main.maxDrawdown - firstCompare.maxDrawdown)
    : null;

  const pills = [
    {
      value: returnDelta != null ? `${formatDelta(returnDelta)}%` : "-",
      label: "return difference",
      color: returnDelta != null && returnDelta >= 0 ? "success.main" : "error.main",
    },
    {
      value: sharpeDelta != null ? formatDelta(sharpeDelta) : "-",
      label: "Sharpe advantage",
      color: sharpeDelta != null && sharpeDelta >= 0 ? "success.main" : "error.main",
    },
    {
      value: drawdownDelta != null ? `${formatDelta(Math.abs(drawdownDelta))}%` : "-",
      label: drawdownDelta != null && drawdownDelta > 0 ? "better drawdown" : "worse drawdown",
      color: drawdownDelta != null && drawdownDelta >= 0 ? "success.main" : "error.main",
    },
  ];

  return (
    <Card
      sx={{
        p: 3,
        py: 5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <EmojiEventsIcon sx={{ color: "background.paper", fontSize: 28 }} />
        </Box>

        <Box flex={1}>
          <Typography variant="h4" gutterBottom>
            Comparison Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
              {leader.title}
            </Typography>
            {" outperforms "}
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
              {trailer.title}
            </Typography>
            {" in "}
            <Typography component="span" variant="body2" color="primary.main" fontWeight={700}>
              {displayWins} of {total}
            </Typography>
            {" key metrics"}
          </Typography>

          <Box display="flex" gap={3} flexWrap="wrap">
            {pills.map((pill) => (
              <Box key={pill.label}>
                <Typography component="span" variant="body2" color={pill.color} fontWeight={700}>
                  {pill.value}
                </Typography>
                {" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  {pill.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ComparisonSummary;
